import { useEffect } from "react";

/** Contact email for privacy requests. */
const CONTACT_EMAIL = "admin@twincityveterans.org";

/**
 * PrivacyPolicy — Public privacy policy page (required for app stores and the
 * public site). Served at /privacy. Content matches the payment-less launch:
 * no online ordering or payment processing, Clover removed.
 *
 * Static content styled per BLU-006 (font-heading/font-body, charcoal/cream/teal).
 */
export default function PrivacyPolicy() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Privacy Policy — Twin City Silver Bullet";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <main
      data-testid="privacy-policy-page"
      className="min-h-screen bg-cream text-ink font-body"
    >
      <article className="max-w-3xl mx-auto px-4 py-16">
        <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">
          LEGAL
        </p>
        <h1 className="font-display text-5xl text-charcoal uppercase mb-3">
          Privacy Policy
        </h1>
        <p className="font-body text-ink/60 mb-8" data-testid="privacy-effective-date">
          Effective date: June 23, 2026
        </p>

        <div className="space-y-8 font-body text-ink/80 text-lg leading-relaxed">
          <p>
            Twin City Silver Bullet ("we," "us," or "our") operates this website.
            This Privacy Policy explains what information we collect, how we use
            it, and your choices.
          </p>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Information We Collect
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Information you provide: When you submit a booking or contact
                request, we collect your name, email address, and any details you
                choose to include (such as an event date or message) so we can
                respond.
              </li>
              <li>
                We do not process online payments through this website at this
                time, and we do not collect payment card information.
              </li>
              <li>
                You do not need to create an account to browse this website.
              </li>
              <li>
                Automatic information: Our hosting and security providers may log
                technical information such as IP address and browser type to
                operate and protect the website. We use Cloudflare Turnstile to
                help prevent spam and abuse on our forms.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              How We Use Information
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to your booking or contact requests.</li>
              <li>To operate, maintain, and secure the website.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              How We Share Information
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>We do not sell your personal information.</li>
              <li>
                We share information only with service providers that help us run
                the website — our email delivery provider (Postmark), website
                hosting (Microsoft Azure), and content delivery and security
                (Cloudflare) — and only as needed to provide the service or as
                required by law.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Cookies and Local Storage
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We use only the cookies and local storage necessary for the
                website to function. We do not use advertising trackers.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Data Retention
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We keep booking and contact messages only as long as needed to
                respond and for our records, then delete them.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Children's Privacy
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                This website is not directed to children under 13, and we do not
                knowingly collect their personal information.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Your Choices
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                To ask what information we have about you, or to request
                deletion, contact us at{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-teal border-b border-teal hover:text-teal-deep transition-colors"
                  data-testid="privacy-contact-email"
                >
                  {CONTACT_EMAIL}
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Changes to This Policy
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We may update this Privacy Policy from time to time. The effective
                date above indicates the latest version.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-charcoal uppercase mb-3">
              Contact
            </h2>
            <p>
              Twin City Silver Bullet —{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal border-b border-teal hover:text-teal-deep transition-colors"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </section>
        </div>
      </article>
    </main>
  );
}
